import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Button, TextField, Typography,
  IconButton, InputAdornment, CircularProgress
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

/* ── Inline SVG educational icons (unchanged from original) ── */
const EduIcons = {
  cap: (s) => (
    <svg width={s} height={s} viewBox="0 0 64 64" fill="none">
      <polygon points="32,8 62,24 32,40 2,24" fill="rgba(150,185,255,0.9)"/>
      <path d="M14 30v16c0 0 8 10 18 10s18-10 18-10V30" fill="rgba(150,185,255,0.55)"/>
      <line x1="62" y1="24" x2="62" y2="42" stroke="rgba(150,185,255,0.8)" strokeWidth="3" strokeLinecap="round"/>
      <circle cx="62" cy="44" r="3" fill="rgba(150,185,255,0.8)"/>
    </svg>
  ),
  book: (s) => (
    <svg width={s} height={s} viewBox="0 0 64 64" fill="none">
      <rect x="4" y="8" width="26" height="48" rx="3" fill="rgba(150,185,255,0.75)"/>
      <rect x="34" y="8" width="26" height="48" rx="3" fill="rgba(150,185,255,0.55)"/>
      <line x1="30" y1="8" x2="30" y2="56" stroke="rgba(150,185,255,0.4)" strokeWidth="2"/>
      <line x1="10" y1="20" x2="24" y2="20" stroke="rgba(10,21,48,0.35)" strokeWidth="2" strokeLinecap="round"/>
      <line x1="10" y1="28" x2="24" y2="28" stroke="rgba(10,21,48,0.35)" strokeWidth="2" strokeLinecap="round"/>
      <line x1="10" y1="36" x2="20" y2="36" stroke="rgba(10,21,48,0.35)" strokeWidth="2" strokeLinecap="round"/>
      <line x1="40" y1="20" x2="54" y2="20" stroke="rgba(10,21,48,0.25)" strokeWidth="2" strokeLinecap="round"/>
      <line x1="40" y1="28" x2="54" y2="28" stroke="rgba(10,21,48,0.25)" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),
  atom: (s) => (
    <svg width={s} height={s} viewBox="0 0 64 64" fill="none">
      <ellipse cx="32" cy="32" rx="28" ry="10" stroke="rgba(150,185,255,0.85)" strokeWidth="2" fill="none"/>
      <ellipse cx="32" cy="32" rx="28" ry="10" stroke="rgba(150,185,255,0.65)" strokeWidth="2" fill="none" transform="rotate(60 32 32)"/>
      <ellipse cx="32" cy="32" rx="28" ry="10" stroke="rgba(150,185,255,0.65)" strokeWidth="2" fill="none" transform="rotate(120 32 32)"/>
      <circle cx="32" cy="32" r="5" fill="rgba(150,185,255,0.95)"/>
    </svg>
  ),
  pencil: (s) => (
    <svg width={s} height={s * 2} viewBox="0 0 32 64" fill="none">
      <rect x="8" y="8" width="16" height="40" rx="2" fill="rgba(150,185,255,0.8)"/>
      <polygon points="8,48 24,48 16,62" fill="rgba(150,185,255,0.65)"/>
      <rect x="8" y="6" width="16" height="6" rx="1" fill="rgba(150,185,255,0.5)"/>
      <line x1="16" y1="10" x2="16" y2="48" stroke="rgba(10,21,48,0.15)" strokeWidth="1"/>
    </svg>
  ),
  trophy: (s) => (
    <svg width={s} height={s} viewBox="0 0 64 64" fill="none">
      <path d="M20 8h24v20a12 12 0 01-24 0V8z" fill="rgba(150,185,255,0.85)"/>
      <path d="M8 10h12v12a6 6 0 01-12 0V10z" fill="rgba(150,185,255,0.5)"/>
      <path d="M44 10h12v12a6 6 0 01-12 0V10z" fill="rgba(150,185,255,0.5)"/>
      <rect x="28" y="40" width="8" height="10" fill="rgba(150,185,255,0.65)"/>
      <rect x="18" y="50" width="28" height="6" rx="2" fill="rgba(150,185,255,0.75)"/>
    </svg>
  ),
  bulb: (s) => (
    <svg width={s} height={s} viewBox="0 0 64 64" fill="none">
      <path d="M32 6a18 18 0 0110 33l-2 4H24l-2-4A18 18 0 0132 6z" fill="rgba(150,185,255,0.8)"/>
      <rect x="24" y="44" width="16" height="4" rx="2" fill="rgba(150,185,255,0.6)"/>
      <rect x="26" y="50" width="12" height="4" rx="2" fill="rgba(150,185,255,0.5)"/>
      <line x1="32" y1="2" x2="32" y2="5" stroke="rgba(150,185,255,0.7)" strokeWidth="2" strokeLinecap="round"/>
      <line x1="52" y1="10" x2="49" y2="13" stroke="rgba(150,185,255,0.7)" strokeWidth="2" strokeLinecap="round"/>
      <line x1="12" y1="10" x2="15" y2="13" stroke="rgba(150,185,255,0.7)" strokeWidth="2" strokeLinecap="round"/>
      <line x1="57" y1="28" x2="53" y2="28" stroke="rgba(150,185,255,0.7)" strokeWidth="2" strokeLinecap="round"/>
      <line x1="7" y1="28" x2="11" y2="28" stroke="rgba(150,185,255,0.7)" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),
  calc: (s) => (
    <svg width={s} height={s * 1.3} viewBox="0 0 48 64" fill="none">
      <rect x="4" y="2" width="40" height="60" rx="5" fill="rgba(150,185,255,0.72)"/>
      <rect x="10" y="8" width="28" height="14" rx="2" fill="rgba(10,21,48,0.28)"/>
      {[36, 48].map(y => [16, 24, 32].map(x => (
        <circle key={`${x}${y}`} cx={x} cy={y} r="3.5" fill="rgba(10,21,48,0.35)"/>
      )))}
    </svg>
  ),
  ruler: (s) => (
    <svg width={s * 0.55} height={s * 2.2} viewBox="0 0 28 72" fill="none">
      <rect x="4" y="2" width="20" height="68" rx="3" fill="rgba(150,185,255,0.72)"/>
      {[10, 18, 26, 34, 42, 50, 58].map((y, i) => (
        <line key={y} x1="4" y1={y} x2={i % 2 === 0 ? "18" : "13"} y2={y}
          stroke="rgba(10,21,48,0.38)" strokeWidth="1.5" strokeLinecap="round"/>
      ))}
    </svg>
  ),
};

const floatingItems = [
  { top: '6%',  left: '4%',   icon: 'cap',    size: 54, opacity: 0.18, rotate: '-15deg', delay: '0s'   },
  { top: '4%',  right: '6%',  icon: 'book',   size: 50, opacity: 0.15, rotate: '12deg',  delay: '0.4s' },
  { top: '36%', left: '2%',   icon: 'pencil', size: 30, opacity: 0.14, rotate: '-30deg', delay: '0.8s' },
  { top: '40%', right: '3%',  icon: 'atom',   size: 46, opacity: 0.14, rotate: '20deg',  delay: '0.2s' },
  { bottom:'9%',left: '5%',   icon: 'calc',   size: 36, opacity: 0.13, rotate: '8deg',   delay: '1s'   },
  { bottom:'7%',right: '5%',  icon: 'trophy', size: 44, opacity: 0.16, rotate: '-10deg', delay: '0.6s' },
  { top: '17%', right: '14%', icon: 'bulb',   size: 36, opacity: 0.12, rotate: '-8deg',  delay: '0.3s' },
  { bottom:'22%',left:'12%',  icon: 'ruler',  size: 28, opacity: 0.11, rotate: '35deg',  delay: '1.2s' },
];

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const accountName = location.state?.accountName || '';
  // accessToken from state is only used for non-teacher/student-panel flows (waralsadmin / default)
  const accessToken = location.state?.accessToken || localStorage.getItem('token');

  /* ────────────────────────────────────────────────────────────
     CHANGED: role -> route mapping now lives in one place.
     student -> /student/dashboard
     teacher -> /teacher/dashboard   (build out when the teacher panel is ready)
     anything else (admin, and any role we haven't split out yet) ->
       /dashboard, same as the original pre-panel-split behavior.
       A dedicated "general login" for non-teacher/student/admin roles
       is planned separately — nothing changes here until that exists.
     waralsadmin account -> /warals/dashboard, unchanged.
  ──────────────────────────────────────────────────────────── */
  const redirectForRole = (role, trimmedAccount) => {
    if (trimmedAccount === 'waralsadmin') {
      navigate('/warals/dashboard');
      return;
    }
    if (role === 'student') {
      navigate('/student/dashboard');
      return;
    }
    if (role === 'teacher') {
      navigate('/teacher/dashboard');
      return;
    }
    // admin, or any other role for now
    navigate('/dashboard');
  };

  /* ────────────────────────────────────────────────────────────
     CHANGED: persists the full login payload (client, user,
     tokens) so every downstream page (Profile, StudentLayout,
     studentApi.js helpers) can read from storage without
     re-fetching.
  ──────────────────────────────────────────────────────────── */
  const persistLoginResponse = (data) => {
    localStorage.setItem('accessToken', data.access);
    localStorage.setItem('refreshToken', data.refresh);
    localStorage.setItem('sessionId', data.session_id);
    localStorage.setItem('client', JSON.stringify(data.client));
    sessionStorage.setItem('user', JSON.stringify(data.user));
  };

  const handleLogin = async () => {
    setError('');

    if (!username || !password) {
      setError('Please enter both username and password');
      return;
    }

    setLoading(true);
    const trimmedAccount = accountName?.toLowerCase();

    try {
      // ── Teacher Panel & Student Panel ──
      // Both roles now come back from the same /api/core/login/ endpoint,
      // distinguished by `user.role` in the response (see sample payload).
      // Always call the API with the entered credentials. Only use the
      // sessionStorage shortcut if the user is ALREADY logged in
      // (i.e. accessToken exists in localStorage, meaning a real session is active).
      if (trimmedAccount === 'teacherpanel' || trimmedAccount === 'studentpanel') {
        const existingToken = localStorage.getItem('accessToken');
        const storedUser = sessionStorage.getItem('user');

        // ── Shortcut: valid active session already exists ──
        if (existingToken && storedUser) {
          let sessionUser;
          try { sessionUser = JSON.parse(storedUser); } catch { sessionUser = null; }

          if (sessionUser?.role) {
            redirectForRole(sessionUser.role, trimmedAccount);
            return;
          }
          // If role is unknown/missing, fall through to fresh API login below
        }

        // ── Fresh login: call API with entered credentials ──
        const response = await fetch(`${API_BASE_URL}/api/core/login/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            // Only attach Bearer if a prior token exists (e.g. multi-account flow)
            ...(existingToken ? { Authorization: `Bearer ${existingToken}` } : {}),
          },
          body: JSON.stringify({ user_id: username, password }),
        });

        if (response.ok) {
          const data = await response.json();
          persistLoginResponse(data);
          redirectForRole(data.user?.role, trimmedAccount);
          return;
        }

        throw new Error('Login failed');
      }

      // ── API Login (waralsadmin or default) ──
      let response;
      if (trimmedAccount === 'waralsadmin') {
        response = await fetch(`${API_BASE_URL}/api/users/login/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: username, password }),
        });
      } else {
        response = await fetch(`${API_BASE_URL}/api/core/login/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ user_id: username, password }),
        });
      }

      if (response.ok) {
        const data = await response.json();
        persistLoginResponse(data);

        // ── Navigate based on account type / role ──
        if (trimmedAccount === 'waralsadmin') {
          navigate('/warals/dashboard');
        } else {
          redirectForRole(data.user?.role, trimmedAccount);
        }
        return;
      }

      throw new Error('Login failed');
    } catch (err) {
      setError('Invalid username or password');
    } finally {
      setLoading(false);
    }
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      backgroundColor: 'rgba(255,255,255,0.06)',
      color: '#fff',
      fontSize: '0.95rem',
      fontFamily: "'Sora', 'Poppins', sans-serif",
      transition: 'background 0.2s ease',
      '& fieldset': {
        borderColor: 'rgba(255,255,255,0.12)',
        borderWidth: '1px',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(255,255,255,0.25)',
      },
      '&.Mui-focused': {
        backgroundColor: 'rgba(255,255,255,0.09)',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#5b8dee',
        borderWidth: '1.5px',
      },
      '&.Mui-error fieldset': {
        borderColor: 'rgba(248,113,113,0.6)',
      },
    },
    '& .MuiInputLabel-root': {
      color: 'rgba(255,255,255,0.35)',
      fontFamily: "'Sora', 'Poppins', sans-serif",
      fontSize: '0.9rem',
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#7aa7f5',
    },
    '& .MuiInputLabel-root.Mui-error': {
      color: '#f87171',
    },
    '& input::placeholder': {
      color: 'rgba(255,255,255,0.3)',
      opacity: 1,
    },
    '& .MuiFormHelperText-root': {
      color: '#f87171',
      fontSize: '0.78rem',
      mt: 0.75,
      fontFamily: "'Sora', 'Poppins', sans-serif",
    },
    '& input': {
      color: '#fff',
    },
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700&display=swap');
        @keyframes floatUpDown {
          0%,100% { transform: translateY(0px) rotate(var(--rot)); }
          50%      { transform: translateY(-16px) rotate(var(--rot)); }
        }
        .edu-float {
          position: absolute;
          pointer-events: none;
          animation: floatUpDown 6s ease-in-out infinite;
          animation-delay: var(--delay);
          filter: drop-shadow(0 6px 18px rgba(78,115,223,0.28));
        }
      `}</style>

      <Box sx={{
        minHeight: '100vh',
        background: 'linear-gradient(160deg, #0d1b4b 0%, #101d3e 40%, #0a1530 100%)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 2,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          width: '600px',
          height: '600px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(78,115,223,0.12) 0%, transparent 70%)',
          top: '-150px',
          right: '-150px',
          pointerEvents: 'none',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          width: '500px',
          height: '500px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(34,74,190,0.10) 0%, transparent 70%)',
          bottom: '-100px',
          left: '-100px',
          pointerEvents: 'none',
        },
      }}>
        {/* Decorative glow dots */}
        <Box sx={{
          position: 'absolute', top: '18%', left: '8%',
          width: 8, height: 8, borderRadius: '50%',
          backgroundColor: 'rgba(78,115,223,0.6)',
          boxShadow: '0 0 18px 6px rgba(78,115,223,0.3)',
        }} />
        <Box sx={{
          position: 'absolute', bottom: '20%', right: '10%',
          width: 6, height: 6, borderRadius: '50%',
          backgroundColor: 'rgba(100,140,255,0.5)',
          boxShadow: '0 0 14px 5px rgba(100,140,255,0.25)',
        }} />

        {/* Dot grid texture */}
        <Box sx={{
          position: 'absolute', inset: 0,
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.055) 1px, transparent 1px)',
          backgroundSize: '38px 38px', pointerEvents: 'none',
        }} />

        {/* Floating educational icons */}
        {floatingItems.map((item, i) => (
          <div key={i} className="edu-float" style={{
            top: item.top, left: item.left, right: item.right, bottom: item.bottom,
            opacity: item.opacity, '--rot': item.rotate, '--delay': item.delay,
          }}>
            {EduIcons[item.icon](item.size)}
          </div>
        ))}

        {/* Back button */}
        <Box
          onClick={() => navigate(-1)}
          sx={{
            position: 'absolute',
            top: { xs: 20, sm: 32 },
            left: { xs: 20, sm: 36 },
            display: 'flex',
            alignItems: 'center',
            gap: 0.8,
            cursor: 'pointer',
            color: 'rgba(255,255,255,0.45)',
            fontFamily: "'Sora', 'Poppins', sans-serif",
            fontSize: '0.82rem',
            fontWeight: 500,
            transition: 'color 0.2s ease',
            zIndex: 10,
            '&:hover': { color: 'rgba(255,255,255,0.8)' },
          }}
        >
          <ArrowBackIosNewIcon sx={{ fontSize: 13 }} />
          Back
        </Box>

        {/* Card */}
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            background: 'rgba(255,255,255,0.04)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            border: '1px solid rgba(255,255,255,0.09)',
            padding: { xs: '36px 28px', sm: '48px 44px' },
            borderRadius: '24px',
            boxShadow: '0 32px 80px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.05) inset',
            width: '100%',
            maxWidth: 440,
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 40px 90px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.07) inset',
            },
          }}
        >
          {/* Logo */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3.5 }}>
            <img
              src={require('../../assets/warals-logo.png')}
              alt="WARALS Logo"
              style={{
                height: 'auto',
                width: '160px',
                maxWidth: '100%',
                filter: 'brightness(0) invert(1)',
                opacity: 0.92,
              }}
            />
          </Box>

          {/* Divider */}
          <Box sx={{
            height: '1px',
            background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.15), transparent)',
            mb: 3.5,
          }} />

          {/* Title */}
          <Typography variant="h5" sx={{
            color: '#ffffff',
            fontWeight: 700,
            letterSpacing: '-0.3px',
            mb: 0.5,
            fontFamily: "'Sora', 'Poppins', sans-serif",
          }}>
            Login
          </Typography>
          <Typography variant="body2" sx={{
            color: 'rgba(255,255,255,0.45)',
            mb: 3.5,
            fontSize: '0.85rem',
            fontFamily: "'Sora', 'Poppins', sans-serif",
            letterSpacing: '0.01em',
          }}>
            as{' '}
            <span style={{ color: '#7aa7f5', fontWeight: 600 }}>
              {accountName ? accountName.toUpperCase() : 'your account'}
            </span>
          </Typography>

          {/* Global error message */}
          {error && (
            <Box sx={{
              mb: 2.5,
              px: 2,
              py: 1.2,
              borderRadius: '10px',
              backgroundColor: 'rgba(248,113,113,0.1)',
              border: '1px solid rgba(248,113,113,0.25)',
            }}>
              <Typography sx={{
                color: '#f87171',
                fontSize: '0.82rem',
                fontFamily: "'Sora', 'Poppins', sans-serif",
                textAlign: 'center',
              }}>
                {error}
              </Typography>
            </Box>
          )}

          {/* Username */}
          <TextField
            fullWidth
            label="Username"
            variant="outlined"
            value={username}
            error={!!error}
            onChange={(e) => { setUsername(e.target.value); setError(''); }}
            onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonOutlineIcon sx={{ color: error ? '#f87171' : 'rgba(255,255,255,0.35)', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{ ...inputSx, mb: 2.5 }}
          />

          {/* Password */}
          <TextField
            fullWidth
            label="Password"
            variant="outlined"
            type={showPassword ? 'text' : 'password'}
            value={password}
            error={!!error}
            onChange={(e) => { setPassword(e.target.value); setError(''); }}
            onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon sx={{ color: error ? '#f87171' : 'rgba(255,255,255,0.35)', fontSize: 20 }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    sx={{ color: 'rgba(255,255,255,0.35)', '&:hover': { color: 'rgba(255,255,255,0.7)' } }}
                  >
                    {showPassword
                      ? <VisibilityOff sx={{ fontSize: 19 }} />
                      : <Visibility sx={{ fontSize: 19 }} />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ ...inputSx, mb: 3 }}
          />

          {/* Sign In Button */}
          <Button
            fullWidth
            variant="contained"
            disabled={loading}
            onClick={handleLogin}
            sx={{
              py: 1.6,
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #5b8dee 0%, #3b5bdb 50%, #2246c4 100%)',
              textTransform: 'none',
              fontSize: '0.97rem',
              fontWeight: 600,
              fontFamily: "'Sora', 'Poppins', sans-serif",
              letterSpacing: '0.02em',
              boxShadow: '0 8px 24px rgba(59,91,219,0.45)',
              border: '1px solid rgba(255,255,255,0.12)',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)',
                transition: 'left 0.5s ease',
              },
              '&:hover::before': { left: '100%' },
              '&:hover': {
                background: 'linear-gradient(135deg, #6b9bf0 0%, #4a6ae8 50%, #2d55d4 100%)',
                boxShadow: '0 12px 30px rgba(59,91,219,0.55)',
                transform: 'translateY(-1px)',
              },
              '&:active': { transform: 'translateY(0)' },
              '&.Mui-disabled': {
                background: 'rgba(255,255,255,0.1)',
                color: 'rgba(255,255,255,0.3)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            {loading
              ? <CircularProgress size={20} sx={{ color: 'rgba(255,255,255,0.5)' }} />
              : 'Sign In'}
          </Button>

          {/* Divider */}
          <Box sx={{
            height: '1px',
            background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.1), transparent)',
            mt: 3.5,
            mb: 2.5,
          }} />

          {/* Support link */}
          <Typography variant="body2" sx={{
            color: 'rgba(255,255,255,0.45)',
            mb: 3.5,
            fontSize: '0.85rem',
            fontFamily: "'Sora', 'Poppins', sans-serif",
            letterSpacing: '0.01em',
          }}>
            as{' '}
            <span style={{ color: '#7aa7f5', fontWeight: 600 }}>
              {accountName ? accountName.toUpperCase() : 'your account'}
            </span>
          </Typography>
        </Box>
      </Box>
    </>
  );
};

export default LoginPage;